import React, { useState, useEffect, useCallback, useRef } from 'react';
import {
  Container, Typography, Box, Paper, TextField, Button, IconButton,
  CircularProgress, Alert, Divider, Avatar, Chip, Grid, Card, CardContent,
  FormControl, InputLabel, Select, MenuItem, FormControlLabel, Checkbox
} from '@mui/material';
import {
  Send as SendIcon, AttachFile as AttachIcon,
  ArrowBack as BackIcon, Refresh as RefreshIcon, Close as CloseIcon
} from '@mui/icons-material';
import { useTranslation } from 'react-i18next';
import { useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { formatDateDisplay } from '../utils/dateFormatter';
import supportService from '../services/supportService';
import supportSocketService from '../services/supportSocketService';
import AttachmentBubble from '../components/support/AttachmentBubble';
import LoadingOverlay from '../components/common/LoadingOverlay';

const STATUS_COLORS = {
  open: 'info',
  in_progress: 'warning',
  waiting_user: 'error',
  waiting_agent: 'default',
  resolved: 'success',
  closed: 'default'
};
const STATUSES = ['open', 'in_progress', 'waiting_user', 'waiting_agent', 'resolved', 'closed'];

const SupportTicketDetailPage = () => {
  const { t } = useTranslation(['support', 'common']);
  const navigate = useNavigate();
  const { id } = useParams();
  const [searchParams] = useSearchParams();
  const magicToken = searchParams.get('token');
  const { user } = useAuth();
  const messagesEndRef = useRef(null);
  const errorRef = useRef(null);

  const [ticket, setTicket] = useState(null);
  const [messages, setMessages] = useState([]);
  const [newMessage, setNewMessage] = useState('');
  const [isInternalNote, setIsInternalNote] = useState(false);
  const [files, setFiles] = useState([]);
  const [loading, setLoading] = useState(true);
  const [sending, setSending] = useState(false);
  const [statusLoading, setStatusLoading] = useState(false);
  const [reopenLoading, setReopenLoading] = useState(false);
  const [error, setError] = useState(null);
  const [isAgent, setIsAgent] = useState(false);
  const [canChangeStatus, setCanChangeStatus] = useState(false);
  const [agentCheckDone, setAgentCheckDone] = useState(false);

  const MAX_FILE_SIZE = 5 * 1024 * 1024; // 5MB

  // Check if user is agent - runs first
  useEffect(() => {
    if (!user || magicToken) {
      setIsAgent(false);
      return;
    }

    const checkAgent = async () => {
      try {
        const agentRes = await supportService.checkUserIsAgent();
        setIsAgent(agentRes?.data?.isAgent || false);
      } catch {
        setIsAgent(false);
      }
    };

    checkAgent();
  }, [user, magicToken]);

  const fetchTicket = useCallback(async () => {
    try {
      if (!id && !magicToken) {
        setError(t('support:errors.ticketNotFound'));
        setLoading(false);
        return;
      }

      let res;
      if (magicToken) {
        res = await supportService.getTicketByToken(magicToken);
      } else if (isAgent) {
        // Agents use their dedicated endpoint
        res = await supportService.getAgentTicket(id);
      } else {
        // Regular users/admins use normal endpoint
        res = await supportService.getTicket(id);
      }
      if (res.success) {
        setTicket(res.data);
        setMessages(res.data.messages || []);
      }
    } catch {
      setError(t('support:errors.loadError'));
    } finally {
      setLoading(false);
    }
  }, [id, magicToken, t, isAgent]);

  useEffect(() => {
    if (!agentCheckDone) return; // Wait for agent check to complete
    fetchTicket();
  }, [fetchTicket, agentCheckDone]);

  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages]);

  useEffect(() => {
    if (ticket && user && !magicToken) {
      supportService.markRead(ticket.id).catch(() => {});
    }
  }, [ticket, user, magicToken]);

  // First: Determine if user is an agent (runs early)
  useEffect(() => {
    if (!user || magicToken) {
      setIsAgent(false);
      setAgentCheckDone(true);
      return;
    }

    const checkAgent = async () => {
      try {
        const agentRes = await supportService.checkUserIsAgent();
        setIsAgent(agentRes?.data?.isAgent || false);
      } catch {
        setIsAgent(false);
      } finally {
        setAgentCheckDone(true);
      }
    };

    checkAgent();
  }, [user, magicToken]);

  // Second: Check permissions to change status (depends on isAgent)
  useEffect(() => {
    if (!user || magicToken || !ticket) {
      setCanChangeStatus(false);
      return;
    }

    if (user.isAdmin) {
      setCanChangeStatus(true);
    } else if (isAgent && ticket.assignedAgentId) {
      setCanChangeStatus(true);
    } else {
      setCanChangeStatus(false);
    }
  }, [user, ticket, magicToken, isAgent]);

  // Listen for real-time messages
  useEffect(() => {
    const handler = (data) => {
      if (data.ticketId === id || data.message?.ticketId === id) {
        fetchTicket();
      }
    };
    supportSocketService.on('new_message', handler);
    return () => supportSocketService.off('new_message');
  }, [id, fetchTicket]);

  // Auto-scroll to error when it appears
  useEffect(() => {
    if (error && errorRef.current) {
      errorRef.current.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }
  }, [error]);

  const handleFileSelect = (e) => {
    const selectedFiles = Array.from(e.target.files || []);
    const validFiles = [];
    let hasError = false;

    selectedFiles.forEach((file) => {
      if (file.size > MAX_FILE_SIZE) {
        setError(
          t('support:errors.fileTooLarge', {
            name: file.name,
            max: '5MB'
          }) || `${file.name} dépasse 5MB. Veuillez sélectionner un fichier plus petit.`
        );
        hasError = true;
      } else {
        validFiles.push(file);
      }
    });

    if (!hasError) {
      setFiles(validFiles);
    } else {
      // Clear previous valid files if any error
      setFiles([]);
    }
  };

  const handleStatusChange = async (status) => {
    setStatusLoading(true);
    try {
      let isAuthorized = false;
      if (user?.isAdmin) {
        isAuthorized = true;
      } else if (isAgent && ticket.assignedAgentId) {
        isAuthorized = true;
      }

      if (!isAuthorized) {
        setError(t('support:errors.notAuthorized') || 'Not authorized to change status');
        setStatusLoading(false);
        return;
      }

      if (isAgent) {
        await supportService.agentUpdateStatus(ticket.id, status);
      } else {
        await supportService.updateTicketStatus(ticket.id, status);
      }
      await fetchTicket();
    } catch (err) {
      setError(err.response?.data?.message || t('support:errors.sendError'));
    } finally {
      setStatusLoading(false);
    }
  };

  const handleSendMessage = async () => {
    if (!newMessage.trim()) return;
    setSending(true);
    try {
      const formData = new FormData();
      formData.append('content', newMessage);
      if (isInternalNote) formData.append('isInternalNote', isInternalNote);
      files.forEach(file => formData.append('attachments', file, file.name));

      let res;
      if (magicToken) {
        res = await supportService.addMessageByToken(ticket.id, magicToken, formData);
      } else if (isAgent) {
        res = await supportService.agentReply(ticket.id, formData);
      } else {
        res = await supportService.addMessage(ticket.id, formData);
      }

      if (res.success) {
        setMessages(prev => [...prev, res.data]);
        setNewMessage('');
        setFiles([]);
        setIsInternalNote(false);
      }
    } catch {
      setError(t('support:errors.sendError'));
    } finally {
      setSending(false);
    }
  };

  const handleReopen = async () => {
    setReopenLoading(true);
    try {
      await supportService.reopenTicket(ticket.id);
      fetchTicket();
    } catch (error) {
      const errorData = error.response?.data;
      
      // If the ticket status may have changed, prompt user to reload
      if (errorData?.requiresRefresh) {
        setError(errorData.message || t('support:errors.onlyResolvedOrClosedCanReopen'));
        // Auto-reload after user sees the message
        setTimeout(() => {
          window.location.reload();
        }, 3000);
      } else {
        // Generic error for other failures
        setError(errorData?.message || t('support:errors.sendError'));
      }
    } finally {
      setReopenLoading(false);
    }
  };

  if (loading) {
    return (
      <Container maxWidth="md" sx={{ py: 4, display: 'flex', justifyContent: 'center' }}>
        <CircularProgress />
      </Container>
    );
  }

  if (!ticket) {
    return (
      <Container maxWidth="md" sx={{ py: 4 }}>
        <Alert severity="error">{t('support:errors.loadError')}</Alert>
      </Container>
    );
  }

  const isClosed = ticket.status === 'closed' || ticket.status === 'resolved';

  return (
    <Container maxWidth="md" sx={{ py: 4 }}>
      {/* Header avec style gradient */}
      <Box sx={{ 
          display: 'flex', 
          flexDirection: 'row',
          alignItems: 'flex-start',
          justifyContent: 'space-between',
          mb: 3,
          py: 2,
          px: 3,
          borderRadius: 2,
          background: 'linear-gradient(45deg, #6200ee 30%, #03dac4 90%)',
          color: 'white',
          boxShadow: '0 4px 20px rgba(0,0,0,0.1)'
        }}>
        {/* Bouton retour */}
        <IconButton 
          onClick={() => {
            if (magicToken) {
              navigate('/');
            } else if (isAgent) {
              navigate('/agent-dashboard');
            } else {
              navigate('/support/tickets');
            }
          }}
          sx={{ 
            color: 'white', 
            mr: 1,
            '&:hover': { bgcolor: 'rgba(255,255,255,0.1)' }
          }}
        >
          <BackIcon />
        </IconButton>

        {/* Contenu principal */}
        <Box sx={{ flex: 1, mr: 2 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1, flexWrap: 'wrap' }}>
            <Typography variant="body2" sx={{ color: 'rgba(255,255,255,0.9)' }}>
              {t('support:ticket.ticketNumber', { number: ticket.ticketNumber })}
            </Typography>
            <Chip
              label={t(`support:status.${ticket.status}`)}
              color={STATUS_COLORS[ticket.status]}
              size="small"
              variant="outlined"
              sx={{ borderColor: 'white', color: 'white' }}
            />
            <Chip
              label={t(`support:categories.${ticket.category}`)}
              size="small"
              variant="outlined"
              sx={{ borderColor: 'white', color: 'white' }}
            />
            {ticket.priority === 'urgent' && (
              <Chip 
                label={t('support:priority.urgent')} 
                size="small" 
                sx={{ bgcolor: 'rgba(255,255,255,0.2)', color: 'white', fontWeight: 600 }}
              />
            )}
          </Box>
          <Typography variant="h5" component="h1" sx={{ fontWeight: 700, mb: 0.5, color: 'white' }}>
            {ticket.subject}
          </Typography>
          <Typography variant="caption" sx={{ color: 'rgba(255,255,255,0.85)' }}>
            {t('support:ticket.createdAt', { date: formatDateDisplay(ticket.createdAt) })}
            {ticket.updatedAt && ` — ${t('support:ticket.lastUpdate', { date: formatDateDisplay(ticket.updatedAt) })}`}
          </Typography>

          {/* Agent online status */}
          {ticket.assignedAgent && (
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mt: 2 }}>
              <Box
                sx={{
                  width: 14, height: 14, borderRadius: '50%',
                  bgcolor: ticket.assignedAgent.isOnline && !ticket.assignedAgent.isPaused
                    ? '#4caf50'
                    : ticket.assignedAgent.isOnline && ticket.assignedAgent.isPaused
                      ? '#ff9800'
                      : 'rgba(255,255,255,0.4)'
                }}
              />
              <Typography variant="subtitle2" sx={{ color: 'rgba(255,255,255,0.9)', fontWeight: 500 }}>
                {ticket.assignedAgent.user?.name || t('support:tickets.agent')}
                {' — '}
                <Typography
                  component="span"
                  variant="subtitle2"
                  sx={{ color: 'rgba(255,255,255,0.85)' }}
                >
                  {ticket.assignedAgent.isOnline && !ticket.assignedAgent.isPaused
                    ? t('support:widget.agentOnline')
                    : ticket.assignedAgent.isOnline && ticket.assignedAgent.isPaused
                      ? t('support:admin.agents.paused')
                      : t('support:widget.agentOffline')
                  }
                </Typography>
              </Typography>
            </Box>
          )}
        </Box>

        {/* Bouton refresh */}
        <IconButton 
          onClick={fetchTicket} 
          title="Refresh"
          sx={{ 
            color: 'white',
            '&:hover': { bgcolor: 'rgba(255,255,255,0.1)' }
          }}
        >
          <RefreshIcon />
        </IconButton>
      </Box>

      {error && <Alert ref={errorRef} severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>{error}</Alert>}

      {/* Status change panel for admin/agent */}
      {canChangeStatus && !magicToken && (
        <Box sx={{ mb: 3, p: 2, bgcolor: 'background.default', borderRadius: 1, border: '1px solid', borderColor: 'divider' }}>
          <FormControl fullWidth size="small" disabled={statusLoading}>
            <InputLabel>{t('support:admin.tickets.changeStatus')}</InputLabel>
            <Select
              value={ticket?.status || ''}
              label={t('support:admin.tickets.changeStatus')}
              onChange={(e) => handleStatusChange(e.target.value)}
            >
              {STATUSES.map(s => (
                <MenuItem key={s} value={s}>
                  <Chip label={t(`support:status.${s}`) || s} size="small" color={STATUS_COLORS[s]} sx={{ cursor: 'pointer' }} />
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        </Box>
      )}

      {/* Messages */}
      <Paper sx={{ p: 0, mb: 2, maxHeight: '60vh', overflow: 'auto' }}>
        {messages.map((msg, idx) => {
          const isUser = msg.senderType === 'user';
          return (
            <Box
              key={msg.id || idx}
              sx={{
                p: 2,
                bgcolor: isUser ? 'primary.50' : 'action.hover',
                borderBottom: idx < messages.length - 1 ? 1 : 0,
                borderColor: 'divider',
                display: 'flex',
                justifyContent: isUser ? 'flex-end' : 'flex-start'
              }}
            >
              <Box sx={{ display: 'flex', gap: 1.5, alignItems: 'flex-start', flexDirection: isUser ? 'row-reverse' : 'row', maxWidth: '80%' }}>
                <Avatar
                  src={msg.sender?.profilePicture || msg.sender?.profile_picture}
                  sx={{
                    width: 32, height: 32, fontSize: 14,
                    bgcolor: isUser ? 'primary.main' : 'secondary.main'
                  }}
                  title={`Sender: ${msg.sender?.name || 'Unknown'} | ProfilePicture: ${msg.sender?.profilePicture || msg.sender?.profile_picture || 'undefined'}`}
                >
                  {isUser ? (msg.sender?.name?.[0] || 'U') : 'A'}
                </Avatar>
                <Box sx={{ flex: 1 }}>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 0.5, flexDirection: isUser ? 'row-reverse' : 'row' }}>
                    <Typography variant="body2" fontWeight={600}>
                      {isUser
                        ? (msg.sender?.name || t('support:ticket.youSent'))
                        : t('support:ticket.agentReply')
                      }
                    </Typography>
                    <Typography variant="caption" color="text.secondary">
                      {formatDateDisplay(msg.createdAt)}
                    </Typography>
                  </Box>
                  <Typography variant="body1" sx={{ whiteSpace: 'pre-wrap', textAlign: isUser ? 'right' : 'left' }}>
                    {msg.content}
                  </Typography>
                  {msg.attachments?.length > 0 && (
                    <Box sx={{ mt: 1.5, display: 'flex', flexDirection: 'column', gap: 1, alignItems: isUser ? 'flex-end' : 'flex-start' }}>
                      {msg.attachments.map((att, i) => (
                        <AttachmentBubble
                          key={i}
                          attachment={att}
                          isFromUser={isUser}
                          index={i}
                        />
                      ))}
                    </Box>
                  )}
                </Box>
              </Box>
            </Box>
          );
        })}
        <div ref={messagesEndRef} />
      </Paper>

      {/* Input area */}
      {isClosed ? (
        <Box sx={{ textAlign: 'center', py: 2 }}>
          {(ticket.status === 'resolved' || ticket.status === 'closed') && !magicToken && (
            <Button variant="outlined" onClick={handleReopen}>
              {t('support:ticket.reopen')}
            </Button>
          )}
        </Box>
      ) : (
        <Paper sx={{ p: 2 }}>
          <TextField
            fullWidth
            multiline
            maxRows={4}
            placeholder={isAgent && isInternalNote ? 'Note interne (visible par les agents uniquement)…' : t('support:ticket.messageInputPlaceholder')}
            value={newMessage}
            onChange={(e) => setNewMessage(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === 'Enter' && !e.shiftKey) {
                e.preventDefault();
                handleSendMessage();
              }
            }}
            size="small"
            sx={{ mb: 1.5 }}
          />
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              {isAgent && (
                <FormControlLabel
                  control={
                    <Checkbox
                      checked={isInternalNote}
                      onChange={(e) => setIsInternalNote(e.target.checked)}
                      size="small"
                    />
                  }
                  label={<Typography variant="body2">{t('support:admin.tickets.internalNote')}</Typography>}
                />
              )}
              <input
                type="file"
                id="ticket-attach"
                multiple
                hidden
                accept="image/jpeg,image/png,image/gif,image/webp,application/pdf"
                onChange={handleFileSelect}
              />
              <IconButton component="label" htmlFor="ticket-attach" title="Max 5MB par fichier">
                <AttachIcon />
              </IconButton>
              <Typography variant="caption" color="textSecondary" sx={{ fontSize: '0.75rem' }}>
                (Max 5MB)
              </Typography>
            </Box>
            <Button
              variant="contained"
              onClick={handleSendMessage}
              disabled={sending || !newMessage.trim()}
              color={isInternalNote ? 'warning' : 'primary'}
            >
              {sending ? <CircularProgress size={20} /> : <SendIcon />}
            </Button>
          </Box>
        </Paper>
      )}

      {files.length > 0 && (
        <Box sx={{ mt: 1.5, display: 'flex', gap: 1, flexWrap: 'wrap' }}>
          {files.map((f, i) => (
            <Box
              key={i}
              sx={{
                display: 'flex',
                alignItems: 'center',
                gap: 1,
                px: 1.5,
                py: 0.75,
                bgcolor: 'primary.light',
                borderRadius: '16px',
                border: '1px solid',
                borderColor: 'primary.main',
                fontSize: '0.85rem',
                fontWeight: 500
              }}
            >
              <AttachIcon sx={{ fontSize: 16, color: 'primary.main' }} />
              <Typography variant="caption" sx={{ flex: 1, fontWeight: 500 }}>
                {f.name.length > 20 ? f.name.substring(0, 17) + '...' : f.name}
              </Typography>
              <IconButton
                size="small"
                onClick={() => setFiles(prev => prev.filter((_, j) => j !== i))}
                sx={{
                  p: '4px',
                  color: 'primary.main',
                  '&:hover': { bgcolor: 'primary.lighter' }
                }}
              >
                <CloseIcon sx={{ fontSize: 16 }} />
              </IconButton>
            </Box>
          ))}
        </Box>
      )}

      <LoadingOverlay
        isOpen={statusLoading || reopenLoading}
        title={reopenLoading
          ? t('support:ticket.reopeningTitle', 'Réouverture en cours...')
          : t('support:ticket.statusChangingTitle', 'Mise à jour du statut...')
        }
        subtitle={reopenLoading
          ? t('support:ticket.reopeningSubtitle', 'Nous rouvrions le ticket. Veuillez patienter...')
          : t('support:ticket.statusChangingSubtitle', 'Nous mettons à jour le statut du ticket. Veuillez patienter...')
        }
        warning={t('support:ticket.doNotClose', 'Ne fermez pas cette page.')}
        primaryColor="#6200ee"
        accentColor="#03dac4"
      />
    </Container>
  );
};

export default SupportTicketDetailPage;
